import { useCallback, useRef, useEffect, useState } from "react";

import "tinymce/tinymce";
import "tinymce/models/dom";
import "tinymce/icons/default";
import "tinymce/themes/silver";
import "tinymce/plugins/preview";
import "tinymce/plugins/searchreplace";
import "tinymce/plugins/autolink";
import "tinymce/plugins/directionality";
import "tinymce/plugins/visualblocks";
import "tinymce/plugins/visualchars";
import "tinymce/plugins/fullscreen";
import "tinymce/plugins/image";
import "tinymce/plugins/link";
import "tinymce/plugins/media";
import "tinymce/plugins/code";
import "tinymce/plugins/codesample";
import "tinymce/plugins/table";
import "tinymce/plugins/charmap";
import "tinymce/plugins/pagebreak";
import "tinymce/plugins/nonbreaking";
import "tinymce/plugins/anchor";
import "tinymce/plugins/insertdatetime";
import "tinymce/plugins/advlist";
import "tinymce/plugins/lists";
import "tinymce/plugins/wordcount";
import { Editor as TinyMCEEditor } from "@tinymce/tinymce-react";

import { useLocale } from "../Intl/Context";
import { useBlocks } from "./blocks";
import KEY_CODES from "../Utils/keyCodes";
import { useInputAriaDescribedBy } from "../Form/useInputAriaDescribedBy";
import { useFieldContext } from "../Form/Field";
import { loadScriptOnce } from "../Utils/loadScriptOnce";
import { getFontStylesheetUrl } from "../Utils/getStylesheetURL";
import { getIsTouchDevice } from "../Utils/ResponsiveQueries";

const Editor = ({
  toolbar = "",
  menubar = "file edit insert view format table tools help",
  contextmenu = "copy paste | link linkchecker | image | table | spellchecker | permanentpen",
  ...props
}) => {
  const {
    onChange,
    imagesUploadHandler,
    value,
    plugins,
    labelNode,
    placeholder,
    onFocus
  } = props;

  const locale = useLocale();
  const blocks = useBlocks();
  const fontStylesheetUrl = getFontStylesheetUrl();

  const editorRef = useRef();
  const { fieldId, setFieldId } = useFieldContext();

  const [isLanguageFileLoaded, setLanguageFileLoaded] = useState(false);

  const setEditorRef = useCallback(
    (_event, editor) => {
      editorRef.current = editor;

      if (fieldId && fieldId !== editor.id) setFieldId(editor.id);

      const iframe = editor.editorContainer.querySelector("iframe");
      iframe.removeAttribute("frameborder");
      iframe.removeAttribute("allowtransparency");

      // If there's a provided label node for the WYSIWYG editor, we set its `for`
      // attribute to the id of the editor.
      if (labelNode && labelNode.tagName === "label") {
        labelNode.setAttribute("for", editor.id);
      }
    },
    [fieldId, labelNode, setFieldId]
  );

  const handleChange = useCallback(newContent => onChange(newContent), [onChange]);

  const handleKeyDown = useCallback(event => {
    if (!editorRef.current) return;

    // Since TinyMCE doesn't make the "Escape" key keydown event bubble from the iframe
    // where it happens, we manually make it bubble by recreating a new event, reusing its
    // original properties, and triggering it on the editor container.
    //
    // This is needed for the escape key to close its parent modal when the editor is
    // inside a modal, for accessibility purposes.
    if (event.keyCode === KEY_CODES.ESCAPE) {
      const newEvent = new KeyboardEvent("keydown", { ...event });
      editorRef.current.editorContainer.dispatchEvent(newEvent);
    }
  }, []);

  // Load TinyMCE locale file
  //
  // Note : Download language packs for every needed locale here :
  //        https://www.tiny.cloud/get-tiny/language-packages/
  useEffect(() => {
    loadScriptOnce(`/tinymce/langs/${locale}.js`, () => {
      setLanguageFileLoaded(true);
    });

    return () => setLanguageFileLoaded(false);
  }, [locale]);

  const ariaDescribedBy = useInputAriaDescribedBy();
  const { isRequired, isInvalid } = useFieldContext();

  useEffect(() => {
    if (!fieldId) return;

    const textAreaEditor = document.getElementById(fieldId);

    if (!textAreaEditor) return;

    const ariaAttributes = {
      "aria-required": isRequired,
      "aria-invalid": isInvalid,
      "aria-describedby": ariaDescribedBy
    };

    // When the editor is first rendered, we create a method on the DOM Node to be used by
    // our specs to avoid using the tinyMCE editor's setContent API that doesn't seem to
    // work correctly for us.
    textAreaEditor.setTinyMCEContent = onChange;
    textAreaEditor.dataset.tinyMceInitialized = true;

    // For each ARIA attribute, check whether we need to add its value or remove it on
    // the editor's <textarea>.
    Object.keys(ariaAttributes).forEach(key => {
      const value = ariaAttributes[key];

      if (value) {
        textAreaEditor.setAttribute(key, value);
      } else if (textAreaEditor.hasAttribute(key)) {
        textAreaEditor.removeAttribute(key);
      }
    });
  }, [ariaDescribedBy, fieldId, isInvalid, isRequired, onChange]);

  const isTouchDevice = getIsTouchDevice();

  // Disable the TinyMCE context menu on touch devices — it conflicts with native
  // long-press context menus and doesn't behave correctly.
  const contextMenuForDevice = isTouchDevice ? false : contextmenu;

  // When the image dialog opens for a new image (empty source field), automatically
  // activate the 3rd tab (Upload) so the user doesn't have to click it manually.
  //
  // We use setTimeout(0) because the dialog DOM isn't available yet when OpenWindow
  // fires.
  //
  const setupImageDialog = useCallback(editor => {
    editor.on("OpenWindow", () => {
      setTimeout(() => {
        const dialogEl = document.querySelector(".tox-dialog");

        if (!dialogEl) return;

        const sourceInput = dialogEl.querySelector("input[type='url']");

        if (!sourceInput || sourceInput.value !== "") return;

        const tabs = dialogEl.querySelectorAll(".tox-dialog__body-nav-item");

        if (tabs[2]) tabs[2].click();
      }, 0);
    });
  }, []);

  // Wait for language configuration file to be loaded before rendering the editor
  if (!isLanguageFileLoaded) return null;

  return (
    <TinyMCEEditor
      value={value}
      height
      placeholder={placeholder}
      init={{
        plugins,
        toolbar,
        menubar,
        contextmenu: contextMenuForDevice,
        blocks: blocks,
        placeholder,
        body_id: "wysiwyg-editor",
        image_advtab: true,
        images_reuse_filename: true,
        file_picker_types: "file image media",
        images_upload_handler: imagesUploadHandler,
        language: locale,
        skin_url: "/tinymce/skins/ui/oxide",
        content_css: [
          "/tinymce/skins/content/default/content.min.css",
          fontStylesheetUrl
        ],
        content_css_cors: true,
        font_family_formats: "NittiGrotesk",
        content_style: "body {font-family: NittiGrotesk, sans-serif;}",
        relative_urls: false,
        remove_script_host: false,
        browser_spellcheck: true,
        styles: {
          small: { inline: "small" }
        },
        statusbar: false,
        branding: false,
        promotion: false,
        license_key: "gpl",
        setup: setupImageDialog
      }}
      onInit={setEditorRef}
      onEditorChange={handleChange}
      onKeyDown={handleKeyDown}
      onFocus={onFocus}
    />
  );
};

export default Editor;
